import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { login as apiLogin, verifyToken } from "../service/authService";

const AuthContext = createContext();

const clearAuthStorage = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("orders");
};

const storedUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    localStorage.removeItem("user");
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(storedUser);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [authStatus, setAuthStatus] = useState(() => localStorage.getItem("token") ? "checking" : "ready");
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(undefined, (error) => {
      const requestedToken = error.config?.headers?.Authorization;
      const currentToken = localStorage.getItem("token");
      if (error.response?.status === 401 && currentToken && requestedToken === `Bearer ${currentToken}` && !error.config?.url?.includes("/auth/verify")) {
        clearAuthStorage();
        setToken(null);
        setUser(null);
        setAuthStatus("ready");
        setSessionExpired(true);
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const verifySession = useCallback(async (currentToken) => {
    if (!currentToken) {
      setAuthStatus("ready");
      return;
    }
    setAuthStatus("checking");
    try {
      const response = await verifyToken(currentToken);
      if (localStorage.getItem("token") !== currentToken) return;
      if (!response.data?.valid || !response.data?.user) throw new Error("Invalid session");
      setUser(response.data.user);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      setAuthStatus("ready");
    } catch (error) {
      if (localStorage.getItem("token") !== currentToken) return;
      if (error?.response?.status === 401 || error?.response?.status === 403 || error?.message === "Invalid session") {
        clearAuthStorage();
        setToken(null);
        setUser(null);
        setAuthStatus("ready");
        setSessionExpired(true);
      } else {
        // A temporary network failure should not sign the customer out.
        setAuthStatus("unavailable");
      }
    }
  }, []);

  useEffect(() => {
    if (token) verifySession(token);
    // Only verify the stored session on mount; retries are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (credentials) => {
    const result = await apiLogin(credentials);
    if (!result?.access_token || !result?.user) throw new Error("Invalid login response");
    localStorage.setItem("token", result.access_token);
    if (result.refresh_token) localStorage.setItem("refreshToken", result.refresh_token);
    localStorage.setItem("user", JSON.stringify(result.user));
    setToken(result.access_token);
    setUser(result.user);
    setAuthStatus("ready");
    setSessionExpired(false);
    return result.user;
  };

  const logout = () => {
    clearAuthStorage();
    localStorage.removeItem("orders");
    setUser(null);
    setToken(null);
    setAuthStatus("ready");
    setSessionExpired(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, authStatus, sessionExpired, login, logout, retryVerification: () => verifySession(token) }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
